import { useCallback, useEffect, useRef, useState } from 'react'

// One-shot geolocation, fetched lazily. The Activities distance filter only
// needs the user's position while the filter is active, so the caller passes
// `active` and we request a fix the first time it flips on.
// Coordinates live in component state only — never persisted (they go stale
// and are cheap to re-fetch).
export type GeoState =
	| { status: 'idle' | 'prompting' | 'denied' | 'unavailable' }
	| { status: 'granted'; lat: number; lng: number }

// 'unknown' where the permission can't be read.
export type GeoPermission = 'granted' | 'prompt' | 'denied' | 'unknown'

// True only in the iOS build (`--mode ios`, client/.env.ios). That build uses
// the native Geolocation plugin: iOS asks once and remembers, where the web
// view's geolocation API asks twice.
//
// Defined here and not in config.ts: Rolldown only leaves the Capacitor chunk
// out of the website build when this constant sits in the same module as the
// dynamic import below.
const NATIVE = import.meta.env.VITE_NATIVE === 'true'

// Resolves to the module, not the plugin: see token.ts.
const plugin = () => import('@capacitor/geolocation')

// iOS never asks again once location is denied, so the iOS build links to
// Hewey's page in Settings. Capacitor hands non-app URLs to the system.
export const openLocationSettings: (() => void) | null = NATIVE
	? () => window.location.assign('app-settings:')
	: null

const OPTIONS = {
	enableHighAccuracy: false,
	timeout: 10_000,
	maximumAge: 300_000,
}

// OPTIONS.timeout doesn't count time spent waiting on a permission prompt, or
// on an OS that never answers one, so this caps the whole lookup.
const GIVE_UP_MS = 20_000

const locateNative = async (): Promise<GeoState> => {
	const { Geolocation } = await plugin()
	try {
		const pos = await Geolocation.getCurrentPosition(OPTIONS)
		return {
			status: 'granted',
			lat: pos.coords.latitude,
			lng: pos.coords.longitude,
		}
	} catch {
		// Checks the permission rather than matching the plugin's error codes.
		const perm = await Geolocation.checkPermissions().catch(() => null)
		return {
			status: perm?.location === 'denied' ? 'denied' : 'unavailable',
		}
	}
}

const locateWeb = (): Promise<GeoState> =>
	new Promise((resolve) => {
		if (!('geolocation' in navigator)) {
			resolve({ status: 'unavailable' })
			return
		}
		navigator.geolocation.getCurrentPosition(
			(pos) =>
				resolve({
					status: 'granted',
					lat: pos.coords.latitude,
					lng: pos.coords.longitude,
				}),
			(err) =>
				resolve({
					status:
						err.code === err.PERMISSION_DENIED
							? 'denied'
							: 'unavailable',
				}),
			OPTIONS,
		)
	})

const locate = (): Promise<GeoState> =>
	Promise.race([
		(NATIVE ? locateNative() : locateWeb()).catch((): GeoState => ({
			status: 'unavailable',
		})),
		new Promise<GeoState>((resolve) =>
			setTimeout(() => resolve({ status: 'unavailable' }), GIVE_UP_MS),
		),
	])

const readPermission = async (): Promise<GeoPermission> => {
	if (NATIVE) {
		const { Geolocation } = await plugin()
		const { location } = await Geolocation.checkPermissions()
		return location === 'prompt-with-rationale' ? 'prompt' : location
	}
	if (!('permissions' in navigator)) return 'unknown'
	const status = await navigator.permissions.query({ name: 'geolocation' })
	return status.state
}

// Reads the geolocation permission so callers can auto-use a location that's
// already been granted instead of asking again. Null until the first read.
export const useGeolocationPermission = (): GeoPermission | null => {
	const [perm, setPerm] = useState<GeoPermission | null>(null)

	useEffect(() => {
		let live = true
		const check = (): void => {
			readPermission()
				.then((next) => {
					if (live) setPerm(next)
				})
				// The plugin rejects when Location Services are off.
				.catch(() => {
					if (live) setPerm('unknown')
				})
		}
		// Re-checks on return to the page, e.g. from iOS Settings.
		const onVisible = (): void => {
			if (document.visibilityState === 'visible') check()
		}
		check()
		document.addEventListener('visibilitychange', onVisible)
		return () => {
			live = false
			document.removeEventListener('visibilitychange', onVisible)
		}
	}, [])

	return perm
}

export const useGeolocation = (
	active: boolean,
): { geo: GeoState; retry: () => void } => {
	const [geo, setGeo] = useState<GeoState>({ status: 'idle' })
	const status = useRef(geo.status)
	status.current = geo.status
	// Guard against re-requesting on every render once we've kicked off (or
	// resolved) a lookup for this activation.
	const requested = useRef(false)
	// Bumped per lookup so a late result can't overwrite a newer one.
	const attempt = useRef(0)

	const retry = useCallback(() => {
		requested.current = true
		const id = ++attempt.current
		setGeo({ status: 'prompting' })
		void locate().then((next) => {
			if (id === attempt.current) setGeo(next)
		})
	}, [])

	useEffect(() => {
		if (active && !requested.current) retry()
	}, [active, retry])

	// Retries a denied lookup on return to the page once access is allowed,
	// e.g. in iOS Settings.
	useEffect(() => {
		const onVisible = (): void => {
			if (document.visibilityState !== 'visible') return
			if (status.current !== 'denied') return
			readPermission()
				.then((next) => {
					if (next === 'granted') retry()
				})
				.catch(() => {})
		}
		document.addEventListener('visibilitychange', onVisible)
		return () => document.removeEventListener('visibilitychange', onVisible)
	}, [retry])

	return { geo, retry }
}
